import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import Layout from './components/Layout'
import LoadingScreen from './components/LoadingScreen'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import { useAuth } from './contexts/AuthContext'

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Listings = lazy(() => import('./pages/Listings'))
const ListingDetail = lazy(() => import('./pages/ListingDetail'))
const Contacts = lazy(() => import('./pages/Contacts'))
const Search = lazy(() => import('./pages/Search'))
const CommercialLease = lazy(() => import('./pages/CommercialLease'))
const LeadSourceDiscovery = lazy(() => import('./pages/LeadSourceDiscovery'))
const PropertyDetail = lazy(() => import('./pages/PropertyDetail'))
const Agents = lazy(() => import('./pages/Agents'))
const AgentDetail = lazy(() => import('./pages/AgentDetail'))
const IndustrialListings = lazy(() => import('./pages/IndustrialListings'))
const IndustrialDetail = lazy(() => import('./pages/IndustrialDetail'))
const MortgageeSales = lazy(() => import('./pages/MortgageeSales'))
const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const Watchlist = lazy(() => import('./pages/Watchlist'))
const Prospects = lazy(() => import('./pages/Prospects'))
const ProspectCommandCenter = lazy(() => import('./pages/ProspectCommandCenter'))
const ProspectMatches = lazy(() => import('./pages/ProspectMatches'))
const ContactDetail = lazy(() => import('./pages/ContactDetail'))
const AmbiguousMatches = lazy(() => import('./pages/AmbiguousMatches'))
const AdminLeadAssignments = lazy(() => import('./pages/AdminLeadAssignments'))
const AdminUsers = lazy(() => import('./pages/AdminUsers'))
const Settings = lazy(() => import('./pages/Settings'))
const Profile = lazy(() => import('./pages/Profile'))
const Sms = lazy(() => import('./pages/Sms'))
const SmsMy = lazy(() => import('./pages/SmsMy'))
const SmsBulkSend = lazy(() => import('./pages/SmsBulkSend'))
const SmsComposePage = lazy(() => import('./pages/SmsComposePage'))
const SmsCampaigns = lazy(() => import('./pages/SmsCampaigns'))
const SmsApiTest = lazy(() => import('./pages/SmsApiTest'))
const MyAssignments = lazy(() => import('./pages/MyAssignments'))
const PublicProspectPage = lazy(() => import('./pages/PublicProspectPage'))
const PublicShortlistPage = lazy(() => import('./pages/PublicShortlistPage'))
const ShortlistPreviewPage = lazy(() => import('./pages/ShortlistPreviewPage'))
const MasterContacts = lazy(() => import('./pages/MasterContacts'))
const MasterContactDetail = lazy(() => import('./pages/MasterContactDetail'))
const LbpBuilders = lazy(() => import('./pages/LbpBuilders'))
const FbLeads = lazy(() => import('./pages/FbLeads'))
const FbLeadAiFollowUp = lazy(() => import('./pages/FbLeadAiFollowUp'))
const MyContacts = lazy(() => import('./pages/MyContacts'))

// Protected Layout wrapper
const ProtectedLayout = ({ children }) => (
  <ProtectedRoute>
    <Layout>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Layout>
  </ProtectedRoute>
)

// Public route that redirects if authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return <LoadingScreen message="Loading..." />
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />
  }
  
  return children
}

function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        
        {/* Public prospect page — no auth required */}
        <Route path="/public/prospect/:token" element={<PublicProspectPage />} />
        
        {/* Public shortlist swipe page — no auth required */}
        <Route path="/shortlist/:token" element={<PublicShortlistPage />} />

        {/* Agent preview of their own shortlist's swipe page — auth required,
            full-screen (no sidebar/header) so it matches what the prospect sees */}
        <Route
          path="/shortlist-preview/:searchId"
          element={
            <ProtectedRoute>
              <ShortlistPreviewPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route path="/" element={<ProtectedLayout><Dashboard /></ProtectedLayout>} />
        <Route path="/listings" element={<ProtectedLayout><LeadSourceDiscovery /></ProtectedLayout>} />
        <Route path="/listings/:id" element={<ProtectedLayout><ListingDetail /></ProtectedLayout>} />
        <Route path="/commercial-lease" element={<ProtectedLayout><CommercialLease /></ProtectedLayout>} />
        <Route path="/commercial-lease/:id" element={<ProtectedLayout><ListingDetail /></ProtectedLayout>} />
        <Route path="/contacts" element={<ProtectedLayout><Contacts /></ProtectedLayout>} />
        <Route path="/contacts/:id" element={<ProtectedLayout><ContactDetail /></ProtectedLayout>} />
        <Route path="/my-contacts" element={<ProtectedLayout><MyContacts /></ProtectedLayout>} />
        <Route path="/master-contacts" element={<ProtectedLayout><ProtectedRoute requireAdmin><MasterContacts /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/master-contacts/:id" element={<ProtectedLayout><ProtectedRoute requireAdmin><MasterContactDetail /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/lbp-builders" element={<ProtectedLayout><ProtectedRoute><LbpBuilders /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/search" element={<ProtectedLayout><ProtectedRoute requireAdmin><Search /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/discovery" element={<ProtectedLayout><LeadSourceDiscovery /></ProtectedLayout>} />
        <Route path="/property/:id" element={<ProtectedLayout><PropertyDetail /></ProtectedLayout>} />
        <Route path="/fb-leads" element={<ProtectedLayout><ProtectedRoute requireAgentsView><FbLeads /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/fb-leads/ai" element={<ProtectedLayout><ProtectedRoute requireAgentsView><FbLeadAiFollowUp /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/agents" element={<ProtectedLayout><ProtectedRoute requireAgentsView><Agents /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/agents/:agentName" element={<ProtectedLayout><ProtectedRoute requireAgentsView><AgentDetail /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/private-listers" element={<ProtectedLayout><ProtectedRoute requireAgentsView><Agents listerType="private" /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/industrial" element={<ProtectedLayout><IndustrialListings /></ProtectedLayout>} />
        <Route path="/industrial/:id" element={<ProtectedLayout><IndustrialDetail /></ProtectedLayout>} />
        <Route path="/watchlist" element={<ProtectedLayout><Watchlist /></ProtectedLayout>} />
        <Route path="/mortgagee-sales" element={<ProtectedLayout><ProtectedRoute requireAdmin><MortgageeSales /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/prospects" element={<ProtectedLayout><Prospects /></ProtectedLayout>} />
        <Route path="/requirements" element={<ProtectedLayout><Prospects /></ProtectedLayout>} />
        <Route path="/requirements/:id" element={<ProtectedLayout><ProspectCommandCenter /></ProtectedLayout>} />
        <Route path="/requirements/:id/matches" element={<ProtectedLayout><ProspectMatches /></ProtectedLayout>} />
        <Route path="/prospects/requirements/:id" element={<ProtectedLayout><ProspectCommandCenter /></ProtectedLayout>} />
        <Route path="/prospects/requirements/:id/matches" element={<ProtectedLayout><ProspectMatches /></ProtectedLayout>} />
        <Route path="/admin/ambiguous-matches" element={<ProtectedLayout><AmbiguousMatches /></ProtectedLayout>} />
        <Route path="/admin/lead-assignments" element={<ProtectedLayout><AdminLeadAssignments /></ProtectedLayout>} />
        <Route path="/admin/users" element={<ProtectedLayout><ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms" element={<ProtectedLayout><ProtectedRoute requireSmsAccess><Sms /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms/my" element={<ProtectedLayout><ProtectedRoute requireSmsAccess><SmsMy /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms/compose/:id" element={<ProtectedLayout><ProtectedRoute requireSmsAccess><SmsComposePage /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms/campaigns" element={<ProtectedLayout><ProtectedRoute requireSmsAccess><SmsCampaigns /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms/bulk" element={<ProtectedLayout><ProtectedRoute requireAdmin><SmsBulkSend /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/sms/api-test" element={<ProtectedLayout><ProtectedRoute requireAdmin><SmsApiTest /></ProtectedRoute></ProtectedLayout>} />
        <Route path="/settings" element={<ProtectedLayout><Settings /></ProtectedLayout>} />
        <Route path="/profile" element={<ProtectedLayout><Profile /></ProtectedLayout>} />
        <Route path="/my-assignments" element={<ProtectedLayout><MyAssignments /></ProtectedLayout>} />
        
        {/* Catch-all 404 */}
        <Route path="*" element={
          <ProtectedLayout>
            <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>404</h1>
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '24px' }}>Page not found</p>
              <a href="/" style={{ padding: '10px 20px', background: '#FF4D00', color: 'white', textDecoration: 'none', borderRadius: '4px' }}>Go Home</a>
            </div>
          </ProtectedLayout>
        } />
      </Routes>
    </Suspense>
  )
}

export default App
